/**
 * Shared queries used by the shell. Module helpers keep their own queries next to their
 * pages but reuse EP (./endpoints) and the Page<T> shape.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { useAuth } from './auth';
import { EP } from './endpoints';
import type { Alert, BranchRef } from './types';

export interface PublicSettings {
  businessName?: string;
  /** FR-ACC-03 idle sign-out, minutes (Admin setting, default 15). */
  idleTimeoutMinutes?: number;
}

export const usePublicSettings = () =>
  useQuery({ queryKey: ['settings', 'public'], queryFn: () => api<PublicSettings>(EP.settingsPublic, { anonymous: true }), staleTime: 10 * 60_000, retry: false });

/** Branches the user may see (the API applies BranchScope). */
export const useBranches = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['branches', user?.id],
    enabled: !!user,
    queryFn: () => api<{ data: BranchRef[] } | BranchRef[]>(EP.branches, { query: { active: true } }).then((r) => (Array.isArray(r) ? r : r.data)),
    staleTime: 5 * 60_000,
  });
};

/** Open alerts for the bell (FR-DSH-03), refreshed every 60 s like the dashboard. */
export const useAlerts = (branchId?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['alerts', branchId ?? 'all'],
    enabled: !!user,
    queryFn: () => api<{ data: Alert[] } | Alert[]>(EP.alerts, { query: { branchId, status: 'OPEN' }, background: true }).then((r) => (Array.isArray(r) ? r : r.data)),
    refetchInterval: 60_000,
    retry: false,
  });
};
